import { type Component, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Checkbox from '~/components/Checkbox'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Select from '~/components/Select'
import Textarea from '~/components/Textarea'
import type { PageSettingsType, SectionStyleType, WidgetPropsType, WidgetType } from '~/services/page.service'

/**
 * Panel de propiedades del elemento seleccionado. Cada tipo de widget expone
 * sólo lo suyo: un botón no tiene columnas y una galería no tiene enlace.
 */

export interface PageInspectorPropsType {
  widget: WidgetType | null
  sectionStyle: SectionStyleType | null
  pageSettings: PageSettingsType
  /** Formularios publicados que se pueden incrustar. */
  formOptions: { value: string; title: string; subtitle?: string }[]
  onPatchWidget: (patch: WidgetPropsType) => void
  onPatchSection: (patch: SectionStyleType) => void
  onPatchPage: (patch: PageSettingsType) => void
}

const ALIGN_OPTIONS = [
  { value: 'left', title: 'Izquierda' },
  { value: 'center', title: 'Centro' },
  { value: 'right', title: 'Derecha' }
]

const TONE_OPTIONS = [
  { value: 'default', title: 'Normal' },
  { value: 'muted', title: 'Suave' },
  { value: 'primary', title: 'Color de marca' }
]

const COLUMN_OPTIONS = [
  { value: '2', title: '2 columnas' },
  { value: '3', title: '3 columnas' },
  { value: '4', title: '4 columnas' }
]

/** Lee un archivo como data URI para guardarlo con la página. */
function readImage(file: File | undefined, onDone: (dataUrl: string) => void, onError: (message: string) => void) {
  if (!file) return
  if (file.size > 1_048_576) {
    onError('La imagen no puede superar 1 MB')
    return
  }
  const reader = new FileReader()
  reader.onload = () => onDone(String(reader.result ?? ''))
  reader.onerror = () => onError('No pudimos leer la imagen')
  reader.readAsDataURL(file)
}

/**
 * Campo de imagen: acepta una URL o un archivo del equipo.
 *
 * El selector de archivos se abre con una `<label>` que envuelve al input, que
 * es la forma nativa y funciona en cualquier navegador. Antes era un botón que
 * llamaba a `click()` sobre un input con `display:none`, y varios navegadores
 * ignoran ese click: el botón no hacía nada.
 */
const ImageField: Component<{ label: string; value?: string; onChange: (value: string) => void; onError: (message: string) => void }> = props => (
  <div class="space-y-2">
    <Input label={props.label} placeholder="https://..." value={props.value ?? ''} onInput={e => props.onChange(e.currentTarget.value)} />
    <div class="flex items-center gap-2">
      <label class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-info-soft px-3 py-2 text-sm font-medium text-info transition-colors hover:bg-info-soft/70">
        <Icon icon="material-symbols-light:upload" size={20} />
        Subir
        <input
          type="file"
          accept="image/*"
          class="sr-only"
          // Se limpia el valor para que elegir dos veces la misma foto vuelva a
          // disparar el evento.
          onClick={e => {
            e.currentTarget.value = ''
          }}
          onChange={e => readImage(e.currentTarget.files?.[0], props.onChange, props.onError)}
        />
      </label>
      <Show when={props.value}>
        <Button size="sm" variant="text" color="danger" onClick={() => props.onChange('')} icon="material-symbols-light:delete-outline">
          Quitar
        </Button>
      </Show>
    </div>
    <Show when={props.value}>
      <img src={props.value} alt="" class="h-24 w-full rounded-lg border border-border object-cover" />
    </Show>
  </div>
)

const COLOR_OPTIONS = [
  { value: 'primary', title: 'Principal' },
  { value: 'secondary', title: 'Secundario' },
  { value: 'success', title: 'Éxito' },
  { value: 'danger', title: 'Peligro' },
  { value: 'warning', title: 'Aviso' },
  { value: 'info', title: 'Información' }
]

const SIZE_OPTIONS = [
  { value: 'sm', title: 'Pequeño' },
  { value: 'md', title: 'Normal' },
  { value: 'lg', title: 'Grande' },
  { value: 'xl', title: 'Muy grande' }
]

/**
 * Editor de listas con título, texto e icono. Lo comparten las ventajas, las
 * preguntas frecuentes, las pestañas y la lista: cambia el rótulo, no la forma.
 */
const ItemsEditor: Component<{
  items: { icon?: string; title?: string; text?: string }[]
  label: string
  withIcon?: boolean
  onChange: (items: { icon?: string; title?: string; text?: string }[]) => void
}> = props => {
  const patchAt = (index: number, value: Partial<{ icon: string; title: string; text: string }>) => props.onChange(props.items.map((item, i) => (i === index ? { ...item, ...value } : item)))

  return (
    <div class="space-y-3">
      <For each={props.items}>
        {(item, index) => (
          <div class="rounded-lg border border-border p-3 space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-foreground-subtle">
                {props.label} {index() + 1}
              </span>
              <Button
                size="xs"
                variant="text"
                color="danger"
                tooltip="Quitar"
                icon="material-symbols-light:delete-outline"
                onClick={() => props.onChange(props.items.filter((_, i) => i !== index()))}
              />
            </div>
            <Input label="Título" value={item.title ?? ''} onInput={e => patchAt(index(), { title: e.currentTarget.value })} />
            <Textarea label="Texto" rows={3} value={item.text ?? ''} onInput={e => patchAt(index(), { text: e.currentTarget.value })} />
            <Show when={props.withIcon !== false}>
              <Input label="Icono" placeholder="material-symbols-light:check-circle-outline" value={item.icon ?? ''} onInput={e => patchAt(index(), { icon: e.currentTarget.value })} />
            </Show>
          </div>
        )}
      </For>
      <Button
        size="sm"
        variant="soft"
        color="info"
        fullWidth
        icon="material-symbols-light:add"
        onClick={() => props.onChange([...props.items, { title: `${props.label} ${props.items.length + 1}`, text: '' }])}
      >
        Añadir {props.label.toLowerCase()}
      </Button>
    </div>
  )
}

/**
 * Editor de valores con etiqueta: cifras, planes y barras de nivel. Cada uso
 * decide qué columnas enseña, que es lo único que cambia entre ellos.
 */
const ValuesEditor: Component<{
  values: { label?: string; value?: string; suffix?: string; highlight?: boolean; features?: string[] }[]
  label: string
  valueLabel: string
  withSuffix?: boolean
  withFeatures?: boolean
  onChange: (values: { label?: string; value?: string; suffix?: string; highlight?: boolean; features?: string[] }[]) => void
}> = props => {
  const patchAt = (index: number, value: Partial<{ label: string; value: string; suffix: string; highlight: boolean; features: string[] }>) =>
    props.onChange(props.values.map((item, i) => (i === index ? { ...item, ...value } : item)))

  return (
    <div class="space-y-3">
      <For each={props.values}>
        {(item, index) => (
          <div class="rounded-lg border border-border p-3 space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-foreground-subtle">
                {props.label} {index() + 1}
              </span>
              <Button
                size="xs"
                variant="text"
                color="danger"
                tooltip="Quitar"
                icon="material-symbols-light:delete-outline"
                onClick={() => props.onChange(props.values.filter((_, i) => i !== index()))}
              />
            </div>
            <Input label="Etiqueta" value={item.label ?? ''} onInput={e => patchAt(index(), { label: e.currentTarget.value })} />
            <Input label={props.valueLabel} value={item.value ?? ''} onInput={e => patchAt(index(), { value: e.currentTarget.value })} />
            <Show when={props.withSuffix}>
              <Input label="Aclaración" value={item.suffix ?? ''} onInput={e => patchAt(index(), { suffix: e.currentTarget.value })} />
            </Show>
            <Show when={props.withFeatures}>
              <Textarea
                label="Incluye (una por línea)"
                rows={4}
                value={(item.features ?? []).join('\n')}
                onInput={e => patchAt(index(), { features: e.currentTarget.value.split('\n').filter(line => line.trim()) })}
              />
              <Checkbox label="Destacar este plan" checked={!!item.highlight} onChange={e => patchAt(index(), { highlight: e.currentTarget.checked })} />
            </Show>
          </div>
        )}
      </For>
      <Button size="sm" variant="soft" color="info" fullWidth icon="material-symbols-light:add" onClick={() => props.onChange([...props.values, { label: '', value: '' }])}>
        Añadir {props.label.toLowerCase()}
      </Button>
    </div>
  )
}

/** Editor de enlaces: barra de navegación, migas de pan y panel lateral. */
const LinksEditor: Component<{ links: { label?: string; href?: string }[]; onChange: (links: { label?: string; href?: string }[]) => void }> = props => {
  const patchAt = (index: number, value: Partial<{ label: string; href: string }>) => props.onChange(props.links.map((link, i) => (i === index ? { ...link, ...value } : link)))

  return (
    <div class="space-y-2">
      <For each={props.links}>
        {(link, index) => (
          <div class="rounded-lg border border-border p-3 space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-foreground-subtle">Enlace {index() + 1}</span>
              <Button
                size="xs"
                variant="text"
                color="danger"
                tooltip="Quitar"
                icon="material-symbols-light:delete-outline"
                onClick={() => props.onChange(props.links.filter((_, i) => i !== index()))}
              />
            </div>
            <Input label="Texto" value={link.label ?? ''} onInput={e => patchAt(index(), { label: e.currentTarget.value })} />
            <Input label="Destino" placeholder="/servicios o https://..." value={link.href ?? ''} onInput={e => patchAt(index(), { href: e.currentTarget.value })} />
          </div>
        )}
      </For>
      <Button size="sm" variant="soft" color="info" fullWidth icon="material-symbols-light:add" onClick={() => props.onChange([...props.links, { label: 'Nuevo enlace', href: '' }])}>
        Añadir enlace
      </Button>
    </div>
  )
}

const PageInspector: Component<PageInspectorPropsType & { onError: (message: string) => void }> = props => {
  const p = () => props.widget?.props ?? {}
  const patch = (value: WidgetPropsType) => props.onPatchWidget(value)

  /** Edición de las listas (galería y ventajas) sin perder el resto del array. */
  const patchItem = <T,>(list: T[], index: number, value: Partial<T>): T[] => list.map((item, i) => (i === index ? { ...item, ...value } : item))

  return (
    <div class="space-y-6">
      <Show when={props.widget}>
        <div class="space-y-4">
          <Show when={props.widget!.type === 'heading'}>
            <Textarea label="Texto" rows={2} value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <Select
              label="Jerarquía"
              options={[
                { value: '1', title: 'Título principal' },
                { value: '2', title: 'Título de sección' },
                { value: '3', title: 'Subtítulo' },
                { value: '4', title: 'Subtítulo pequeño' }
              ]}
              value={String(p().level ?? 2)}
              onChange={v => patch({ level: Number(v) as 1 | 2 | 3 | 4 })}
            />
          </Show>

          <Show when={props.widget!.type === 'text'}>
            <Textarea label="Contenido" rows={6} value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <Select
              label="Tamaño"
              options={[
                { value: 'sm', title: 'Pequeño' },
                { value: 'md', title: 'Normal' },
                { value: 'lg', title: 'Grande' }
              ]}
              value={p().size ?? 'md'}
              onChange={v => patch({ size: v as 'sm' | 'md' | 'lg' })}
            />
          </Show>

          <Show when={props.widget!.type === 'heading' || props.widget!.type === 'text'}>
            <Select label="Alineación" options={ALIGN_OPTIONS} value={p().align ?? 'left'} onChange={v => patch({ align: v as 'left' | 'center' | 'right' })} />
            <Select label="Color" options={TONE_OPTIONS} value={p().tone ?? 'default'} onChange={v => patch({ tone: v as 'default' | 'muted' | 'primary' })} />
          </Show>

          <Show when={props.widget!.type === 'image'}>
            <ImageField label="Imagen" value={p().src} onChange={value => patch({ src: value })} onError={props.onError} />
            <Input label="Texto alternativo" value={p().alt ?? ''} onInput={e => patch({ alt: e.currentTarget.value })} />
            <Input label="Ancho máximo (px)" type="number" min="0" value={p().maxWidth ?? ''} onInput={e => patch({ maxWidth: Number(e.currentTarget.value) || undefined })} />
            <Select label="Alineación" options={ALIGN_OPTIONS} value={p().align ?? 'center'} onChange={v => patch({ align: v as 'left' | 'center' | 'right' })} />
            <Checkbox label="Esquinas redondeadas" checked={p().rounded !== false} onChange={e => patch({ rounded: e.currentTarget.checked })} />
          </Show>

          <Show when={props.widget!.type === 'gallery'}>
            <Select label="Columnas" options={COLUMN_OPTIONS} value={String(p().columns ?? 3)} onChange={v => patch({ columns: Number(v) as 2 | 3 | 4 })} />
            <Checkbox label="Esquinas redondeadas" checked={p().rounded !== false} onChange={e => patch({ rounded: e.currentTarget.checked })} />

            <div class="space-y-3">
              <For each={p().images ?? []}>
                {(image, index) => (
                  <div class="rounded-lg border border-border p-3 space-y-2">
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-medium text-foreground-subtle">Imagen {index() + 1}</span>
                      <Button
                        size="xs"
                        variant="text"
                        color="danger"
                        tooltip="Quitar"
                        icon="material-symbols-light:delete-outline"
                        onClick={() => patch({ images: (p().images ?? []).filter((_, i) => i !== index()) })}
                      />
                    </div>
                    <ImageField label="Archivo o URL" value={image.src} onChange={value => patch({ images: patchItem(p().images ?? [], index(), { src: value }) })} onError={props.onError} />
                    <Input label="Pie de foto" value={image.caption ?? ''} onInput={e => patch({ images: patchItem(p().images ?? [], index(), { caption: e.currentTarget.value }) })} />
                  </div>
                )}
              </For>
              <Button size="sm" variant="soft" color="info" fullWidth icon="material-symbols-light:add" onClick={() => patch({ images: [...(p().images ?? []), { src: '', alt: '' }] })}>
                Añadir imagen
              </Button>
            </div>
          </Show>

          <Show when={props.widget!.type === 'video'}>
            <Input label="Enlace del video" placeholder="https://youtube.com/watch?v=..." value={p().url ?? ''} onInput={e => patch({ url: e.currentTarget.value })} />
            <p class="text-xs text-foreground-subtle">Funciona con YouTube y Vimeo.</p>
          </Show>

          <Show when={props.widget!.type === 'button'}>
            <Input label="Texto del botón" value={p().label ?? ''} onInput={e => patch({ label: e.currentTarget.value })} />
            <Input label="Enlace" placeholder="https://... o /contacto" value={p().href ?? ''} onInput={e => patch({ href: e.currentTarget.value })} />
            <Select
              label="Color"
              options={[
                { value: 'primary', title: 'Principal' },
                { value: 'secondary', title: 'Secundario' },
                { value: 'success', title: 'Éxito' },
                { value: 'danger', title: 'Peligro' },
                { value: 'warning', title: 'Aviso' },
                { value: 'info', title: 'Información' }
              ]}
              value={p().color ?? 'primary'}
              onChange={v => patch({ color: v as WidgetPropsType['color'] })}
            />
            <Select
              label="Estilo"
              options={[
                { value: 'contained', title: 'Relleno' },
                { value: 'outlined', title: 'Borde' },
                { value: 'soft', title: 'Suave' },
                { value: 'text', title: 'Sólo texto' }
              ]}
              value={p().variant ?? 'contained'}
              onChange={v => patch({ variant: v as WidgetPropsType['variant'] })}
            />
            <Select
              label="Tamaño"
              options={[
                { value: 'sm', title: 'Pequeño' },
                { value: 'md', title: 'Normal' },
                { value: 'lg', title: 'Grande' }
              ]}
              value={p().buttonSize ?? 'md'}
              onChange={v => patch({ buttonSize: v as 'sm' | 'md' | 'lg' })}
            />
            <Select label="Alineación" options={ALIGN_OPTIONS} value={p().align ?? 'left'} onChange={v => patch({ align: v as 'left' | 'center' | 'right' })} />
            <Checkbox label="Abrir en una pestaña nueva" checked={!!p().newTab} onChange={e => patch({ newTab: e.currentTarget.checked })} />
          </Show>

          <Show when={props.widget!.type === 'form'}>
            <Show when={props.formOptions.length > 0} fallback={<p class="text-sm text-foreground-subtle">Aún no tienes formularios publicados. Publica uno y podrás incrustarlo aquí.</p>}>
              <Select label="Formulario" options={props.formOptions} value={p().formSlug ?? ''} onChange={v => patch({ formSlug: String(v ?? '') })} />
            </Show>
            <Checkbox label="Mostrar título del formulario" checked={p().showFormTitle !== false} onChange={e => patch({ showFormTitle: e.currentTarget.checked })} />
            <Show when={p().showFormTitle !== false}>
              <Input label="Título propio (opcional)" placeholder="Deja vacío para usar el del formulario" value={p().formTitle ?? ''} onInput={e => patch({ formTitle: e.currentTarget.value })} />
            </Show>
          </Show>

          <Show when={props.widget!.type === 'features'}>
            <Select label="Columnas" options={COLUMN_OPTIONS} value={String(p().columns ?? 3)} onChange={v => patch({ columns: Number(v) as 2 | 3 | 4 })} />
            <div class="space-y-3">
              <For each={p().items ?? []}>
                {(item, index) => (
                  <div class="rounded-lg border border-border p-3 space-y-2">
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-medium text-foreground-subtle">Ventaja {index() + 1}</span>
                      <Button
                        size="xs"
                        variant="text"
                        color="danger"
                        tooltip="Quitar"
                        icon="material-symbols-light:delete-outline"
                        onClick={() => patch({ items: (p().items ?? []).filter((_, i) => i !== index()) })}
                      />
                    </div>
                    <Input label="Título" value={item.title ?? ''} onInput={e => patch({ items: patchItem(p().items ?? [], index(), { title: e.currentTarget.value }) })} />
                    <Textarea label="Texto" rows={2} value={item.text ?? ''} onInput={e => patch({ items: patchItem(p().items ?? [], index(), { text: e.currentTarget.value }) })} />
                    <Input
                      label="Icono"
                      placeholder="material-symbols-light:bolt-outline"
                      value={item.icon ?? ''}
                      onInput={e => patch({ items: patchItem(p().items ?? [], index(), { icon: e.currentTarget.value }) })}
                    />
                  </div>
                )}
              </For>
              <Button size="sm" variant="soft" color="info" fullWidth icon="material-symbols-light:add" onClick={() => patch({ items: [...(p().items ?? []), { title: 'Nueva ventaja', text: '' }] })}>
                Añadir ventaja
              </Button>
            </div>
          </Show>

          <Show when={props.widget!.type === 'testimonials'}>
            <Select label="Columnas" options={COLUMN_OPTIONS} value={String(p().columns ?? 3)} onChange={v => patch({ columns: Number(v) as 2 | 3 | 4 })} />
            <div class="space-y-3">
              <For each={p().quotes ?? []}>
                {(quote, index) => (
                  <div class="rounded-lg border border-border p-3 space-y-2">
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-medium text-foreground-subtle">Comentario {index() + 1}</span>
                      <Button
                        size="xs"
                        variant="text"
                        color="danger"
                        tooltip="Quitar"
                        icon="material-symbols-light:delete-outline"
                        onClick={() => patch({ quotes: (p().quotes ?? []).filter((_, i) => i !== index()) })}
                      />
                    </div>
                    <Textarea label="Comentario" rows={3} value={quote.text ?? ''} onInput={e => patch({ quotes: patchItem(p().quotes ?? [], index(), { text: e.currentTarget.value }) })} />
                    <Input label="Quién lo dice" value={quote.author ?? ''} onInput={e => patch({ quotes: patchItem(p().quotes ?? [], index(), { author: e.currentTarget.value }) })} />
                    <Input label="Cargo o empresa" value={quote.role ?? ''} onInput={e => patch({ quotes: patchItem(p().quotes ?? [], index(), { role: e.currentTarget.value }) })} />
                    <Input
                      label="Estrellas (0 a 5)"
                      type="number"
                      min="0"
                      max="5"
                      value={quote.rating ?? 5}
                      onInput={e => patch({ quotes: patchItem(p().quotes ?? [], index(), { rating: Math.min(5, Math.max(0, Number(e.currentTarget.value) || 0)) }) })}
                    />
                    <ImageField label="Foto (opcional)" value={quote.avatar} onChange={value => patch({ quotes: patchItem(p().quotes ?? [], index(), { avatar: value }) })} onError={props.onError} />
                  </div>
                )}
              </For>
              <Button
                size="sm"
                variant="soft"
                color="info"
                fullWidth
                icon="material-symbols-light:add"
                onClick={() => patch({ quotes: [...(p().quotes ?? []), { text: '', author: '', role: '', rating: 5 }] })}
              >
                Añadir comentario
              </Button>
            </div>
          </Show>

          <Show when={props.widget!.type === 'markdown'}>
            <Textarea label="Contenido en Markdown" rows={12} value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <p class="text-xs text-foreground-subtle">Admite títulos, listas, negritas y enlaces.</p>
          </Show>

          <Show when={props.widget!.type === 'card'}>
            <Input label="Título" value={p().title ?? ''} onInput={e => patch({ title: e.currentTarget.value })} />
            <Textarea label="Texto" rows={4} value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <ImageField label="Imagen (opcional)" value={p().src} onChange={value => patch({ src: value })} onError={props.onError} />
            <Input label="Texto del botón (opcional)" value={p().label ?? ''} onInput={e => patch({ label: e.currentTarget.value })} />
            <Show when={p().label}>
              <Input label="Enlace del botón" value={p().href ?? ''} onInput={e => patch({ href: e.currentTarget.value })} />
            </Show>
            <Select label="Color" options={COLOR_OPTIONS} value={p().color ?? 'primary'} onChange={v => patch({ color: v as WidgetPropsType['color'] })} />
          </Show>

          <Show when={props.widget!.type === 'alert'}>
            <Select label="Tipo" options={COLOR_OPTIONS} value={p().color ?? 'info'} onChange={v => patch({ color: v as WidgetPropsType['color'] })} />
            <Input label="Título" value={p().title ?? ''} onInput={e => patch({ title: e.currentTarget.value })} />
            <Textarea label="Mensaje" rows={3} value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <Input label="Icono (opcional)" placeholder="material-symbols-light:info-outline" value={p().icon ?? ''} onInput={e => patch({ icon: e.currentTarget.value })} />
            <Checkbox label="Sólo borde, sin relleno" checked={!!p().outlined} onChange={e => patch({ outlined: e.currentTarget.checked })} />
          </Show>

          <Show when={props.widget!.type === 'accordion'}>
            <ItemsEditor items={p().items ?? []} label="Pregunta" onChange={items => patch({ items })} />
          </Show>

          <Show when={props.widget!.type === 'list'}>
            <ItemsEditor items={p().items ?? []} label="Punto" onChange={items => patch({ items })} />
          </Show>

          <Show when={props.widget!.type === 'tabs'}>
            <Select
              label="Estilo"
              options={[
                { value: 'underline', title: 'Subrayado' },
                { value: 'pills', title: 'Píldoras' },
                { value: 'buttons', title: 'Botones' },
                { value: 'default', title: 'Clásico' }
              ]}
              value={p().tabsVariant ?? 'underline'}
              onChange={v => patch({ tabsVariant: v as WidgetPropsType['tabsVariant'] })}
            />
            <ItemsEditor items={p().items ?? []} label="Pestaña" onChange={items => patch({ items })} />
          </Show>

          <Show when={props.widget!.type === 'chips'}>
            <Textarea
              label="Etiquetas (una por línea)"
              rows={5}
              value={(p().tags ?? []).join('\n')}
              onInput={e =>
                patch({
                  tags: e.currentTarget.value
                    .split('\n')
                    .map(tag => tag.trim())
                    .filter(Boolean)
                })
              }
            />
            <Select label="Color" options={COLOR_OPTIONS} value={p().color ?? 'primary'} onChange={v => patch({ color: v as WidgetPropsType['color'] })} />
            <Select label="Alineación" options={ALIGN_OPTIONS} value={p().align ?? 'left'} onChange={v => patch({ align: v as 'left' | 'center' | 'right' })} />
            <Checkbox label="Sólo borde" checked={!!p().outlined} onChange={e => patch({ outlined: e.currentTarget.checked })} />
          </Show>

          <Show when={props.widget!.type === 'profile'}>
            <ImageField label="Foto" value={p().src} onChange={value => patch({ src: value })} onError={props.onError} />
            <Input label="Nombre" value={p().title ?? ''} onInput={e => patch({ title: e.currentTarget.value })} />
            <Input label="Cargo o especialidad" value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <Select label="Alineación" options={ALIGN_OPTIONS} value={p().align ?? 'center'} onChange={v => patch({ align: v as 'left' | 'center' | 'right' })} />
          </Show>

          <Show when={props.widget!.type === 'table'}>
            <Textarea
              label="Encabezados (uno por línea)"
              rows={4}
              value={(p().headers ?? []).join('\n')}
              onInput={e =>
                patch({
                  headers: e.currentTarget.value.split('\n').map(header => header.trim())
                })
              }
            />
            <Textarea
              label="Filas (una por línea, celdas separadas por |)"
              rows={6}
              value={(p().rows ?? []).map(row => row.join(' | ')).join('\n')}
              onInput={e =>
                patch({
                  rows: e.currentTarget.value
                    .split('\n')
                    .filter(line => line.trim())
                    .map(line => line.split('|').map(cell => cell.trim()))
                })
              }
            />
            <Checkbox label="Filas alternas sombreadas" checked={p().striped !== false} onChange={e => patch({ striped: e.currentTarget.checked })} />
          </Show>

          <Show when={props.widget!.type === 'navbar'}>
            <Input label="Marca" value={p().brand ?? ''} onInput={e => patch({ brand: e.currentTarget.value })} />
            <ImageField label="Logo (opcional)" value={p().src} onChange={value => patch({ src: value })} onError={props.onError} />
            <LinksEditor links={p().links ?? []} onChange={links => patch({ links })} />
            <Input label="Botón destacado" placeholder="Deja vacío para no mostrarlo" value={p().label ?? ''} onInput={e => patch({ label: e.currentTarget.value })} />
            <Show when={p().label}>
              <Input label="Enlace del botón" value={p().href ?? ''} onInput={e => patch({ href: e.currentTarget.value })} />
            </Show>
            <Checkbox label="Fijar arriba al bajar" checked={!!p().sticky} onChange={e => patch({ sticky: e.currentTarget.checked })} />
          </Show>

          <Show when={props.widget!.type === 'breadcrumbs'}>
            <LinksEditor links={p().links ?? []} onChange={links => patch({ links })} />
          </Show>

          <Show when={props.widget!.type === 'modal' || props.widget!.type === 'drawer'}>
            <Input label="Texto del botón" value={p().label ?? ''} onInput={e => patch({ label: e.currentTarget.value })} />
            <Input label="Título del panel" value={p().title ?? ''} onInput={e => patch({ title: e.currentTarget.value })} />
            <Textarea label="Contenido" rows={5} value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <Select label="Tamaño" options={SIZE_OPTIONS} value={p().panelSize ?? 'md'} onChange={v => patch({ panelSize: v as WidgetPropsType['panelSize'] })} />
            <Show when={props.widget!.type === 'drawer'}>
              <Select
                label="Lado"
                options={[
                  { value: 'right', title: 'Derecha' },
                  { value: 'left', title: 'Izquierda' },
                  { value: 'top', title: 'Arriba' },
                  { value: 'bottom', title: 'Abajo' }
                ]}
                value={p().position ?? 'right'}
                onChange={v => patch({ position: v as WidgetPropsType['position'] })}
              />
              <LinksEditor links={p().links ?? []} onChange={links => patch({ links })} />
            </Show>
            <Select label="Color del botón" options={COLOR_OPTIONS} value={p().color ?? 'primary'} onChange={v => patch({ color: v as WidgetPropsType['color'] })} />
            <Select label="Alineación" options={ALIGN_OPTIONS} value={p().align ?? 'left'} onChange={v => patch({ align: v as 'left' | 'center' | 'right' })} />
          </Show>

          <Show when={props.widget!.type === 'hero' || props.widget!.type === 'cta'}>
            <Input label="Titular" value={p().title ?? ''} onInput={e => patch({ title: e.currentTarget.value })} />
            <Textarea label="Texto" rows={3} value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <Input label="Botón principal" value={p().label ?? ''} onInput={e => patch({ label: e.currentTarget.value })} />
            <Input label="Enlace del botón" value={p().href ?? ''} onInput={e => patch({ href: e.currentTarget.value })} />
            <Show when={props.widget!.type === 'hero'}>
              <Input label="Botón secundario" value={p().secondaryLabel ?? ''} onInput={e => patch({ secondaryLabel: e.currentTarget.value })} />
              <Input label="Enlace del secundario" value={p().secondaryHref ?? ''} onInput={e => patch({ secondaryHref: e.currentTarget.value })} />
              <ImageField label="Imagen (opcional)" value={p().src} onChange={value => patch({ src: value })} onError={props.onError} />
            </Show>
            <Select label="Color" options={COLOR_OPTIONS} value={p().color ?? 'primary'} onChange={v => patch({ color: v as WidgetPropsType['color'] })} />
            <Select
              label="Alineación"
              options={ALIGN_OPTIONS}
              value={p().align ?? (props.widget!.type === 'cta' ? 'center' : 'left')}
              onChange={v => patch({ align: v as 'left' | 'center' | 'right' })}
            />
          </Show>

          <Show when={props.widget!.type === 'stats'}>
            <Select label="Columnas" options={COLUMN_OPTIONS} value={String(p().columns ?? 3)} onChange={v => patch({ columns: Number(v) as 2 | 3 | 4 })} />
            <ValuesEditor values={p().values ?? []} label="Cifra" valueLabel="Número" onChange={values => patch({ values })} />
          </Show>

          <Show when={props.widget!.type === 'pricing'}>
            <Select label="Columnas" options={COLUMN_OPTIONS} value={String(p().columns ?? 3)} onChange={v => patch({ columns: Number(v) as 2 | 3 | 4 })} />
            <Input label="Texto del botón" value={p().label ?? ''} onInput={e => patch({ label: e.currentTarget.value })} />
            <Input label="Enlace del botón" value={p().href ?? ''} onInput={e => patch({ href: e.currentTarget.value })} />
            <ValuesEditor values={p().values ?? []} label="Plan" valueLabel="Precio" withSuffix withFeatures onChange={values => patch({ values })} />
          </Show>

          <Show when={props.widget!.type === 'progress'}>
            <ValuesEditor values={p().values ?? []} label="Barra" valueLabel="Porcentaje (0 a 100)" onChange={values => patch({ values })} />
          </Show>

          <Show when={props.widget!.type === 'steps'}>
            <ItemsEditor items={p().items ?? []} label="Paso" onChange={items => patch({ items })} />
          </Show>

          <Show when={props.widget!.type === 'contact'}>
            <Select label="Columnas" options={COLUMN_OPTIONS} value={String(p().columns ?? 3)} onChange={v => patch({ columns: Number(v) as 2 | 3 | 4 })} />
            <ItemsEditor items={p().items ?? []} label="Dato" onChange={items => patch({ items })} />
          </Show>

          <Show when={props.widget!.type === 'team'}>
            <Select label="Columnas" options={COLUMN_OPTIONS} value={String(p().columns ?? 3)} onChange={v => patch({ columns: Number(v) as 2 | 3 | 4 })} />
            <div class="space-y-3">
              <For each={p().quotes ?? []}>
                {(person, index) => (
                  <div class="rounded-lg border border-border p-3 space-y-2">
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-medium text-foreground-subtle">Persona {index() + 1}</span>
                      <Button
                        size="xs"
                        variant="text"
                        color="danger"
                        tooltip="Quitar"
                        icon="material-symbols-light:delete-outline"
                        onClick={() => patch({ quotes: (p().quotes ?? []).filter((_, i) => i !== index()) })}
                      />
                    </div>
                    <Input label="Nombre" value={person.author ?? ''} onInput={e => patch({ quotes: patchItem(p().quotes ?? [], index(), { author: e.currentTarget.value }) })} />
                    <Input label="Cargo" value={person.role ?? ''} onInput={e => patch({ quotes: patchItem(p().quotes ?? [], index(), { role: e.currentTarget.value }) })} />
                    <ImageField label="Foto" value={person.avatar} onChange={value => patch({ quotes: patchItem(p().quotes ?? [], index(), { avatar: value }) })} onError={props.onError} />
                  </div>
                )}
              </For>
              <Button size="sm" variant="soft" color="info" fullWidth icon="material-symbols-light:add" onClick={() => patch({ quotes: [...(p().quotes ?? []), { author: 'Nombre', role: 'Cargo' }] })}>
                Añadir persona
              </Button>
            </div>
          </Show>

          <Show when={props.widget!.type === 'logos'}>
            <Input label="Título" value={p().title ?? ''} onInput={e => patch({ title: e.currentTarget.value })} />
            <div class="space-y-3">
              <For each={p().images ?? []}>
                {(logo, index) => (
                  <div class="rounded-lg border border-border p-3 space-y-2">
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-medium text-foreground-subtle">Logo {index() + 1}</span>
                      <Button
                        size="xs"
                        variant="text"
                        color="danger"
                        tooltip="Quitar"
                        icon="material-symbols-light:delete-outline"
                        onClick={() => patch({ images: (p().images ?? []).filter((_, i) => i !== index()) })}
                      />
                    </div>
                    <ImageField label="Archivo o URL" value={logo.src} onChange={value => patch({ images: patchItem(p().images ?? [], index(), { src: value }) })} onError={props.onError} />
                    <Input label="Nombre del cliente" value={logo.alt ?? ''} onInput={e => patch({ images: patchItem(p().images ?? [], index(), { alt: e.currentTarget.value }) })} />
                  </div>
                )}
              </For>
              <Button size="sm" variant="soft" color="info" fullWidth icon="material-symbols-light:add" onClick={() => patch({ images: [...(p().images ?? []), { src: '', alt: '' }] })}>
                Añadir logo
              </Button>
            </div>
          </Show>

          <Show when={props.widget!.type === 'quote'}>
            <Textarea label="Cita" rows={4} value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <Input label="Quién lo dice" value={p().title ?? ''} onInput={e => patch({ title: e.currentTarget.value })} />
            <Select label="Alineación" options={ALIGN_OPTIONS} value={p().align ?? 'center'} onChange={v => patch({ align: v as 'left' | 'center' | 'right' })} />
          </Show>

          <Show when={props.widget!.type === 'social' || props.widget!.type === 'buttons' || props.widget!.type === 'footer'}>
            <Show when={props.widget!.type === 'footer'}>
              <Input label="Marca" value={p().brand ?? ''} onInput={e => patch({ brand: e.currentTarget.value })} />
              <Input label="Texto legal" value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            </Show>
            <LinksEditor links={p().links ?? []} onChange={links => patch({ links })} />
            <Show when={props.widget!.type !== 'footer'}>
              <Select label="Color" options={COLOR_OPTIONS} value={p().color ?? 'primary'} onChange={v => patch({ color: v as WidgetPropsType['color'] })} />
              <Select label="Alineación" options={ALIGN_OPTIONS} value={p().align ?? 'left'} onChange={v => patch({ align: v as 'left' | 'center' | 'right' })} />
            </Show>
            <Show when={props.widget!.type === 'social'}>
              <p class="text-xs text-foreground-subtle">El icono de cada red se toma del campo "Icono" del enlace; por defecto se usan los de Instagram, LinkedIn y WhatsApp.</p>
            </Show>
          </Show>

          <Show when={props.widget!.type === 'map'}>
            <Input label="Lugar o dirección" placeholder="Cra 43A #1-50, Medellín" value={p().text ?? ''} onInput={e => patch({ text: e.currentTarget.value })} />
            <Input label="Alto (px)" type="number" min="120" value={p().embedHeight ?? 320} onInput={e => patch({ embedHeight: Number(e.currentTarget.value) || 320 })} />
          </Show>

          <Show when={props.widget!.type === 'embed'}>
            <Input label="Dirección a incrustar" placeholder="https://..." value={p().url ?? ''} onInput={e => patch({ url: e.currentTarget.value })} />
            <Input label="Alto (px)" type="number" min="120" value={p().embedHeight ?? 420} onInput={e => patch({ embedHeight: Number(e.currentTarget.value) || 420 })} />
          </Show>

          <Show when={props.widget!.type === 'code'}>
            <Input label="Título" value={p().title ?? ''} onInput={e => patch({ title: e.currentTarget.value })} />
            <Textarea label="Código" rows={10} value={p().code ?? ''} onInput={e => patch({ code: e.currentTarget.value })} />
          </Show>

          <Show when={props.widget!.type === 'icon'}>
            <Input label="Icono" placeholder="material-symbols-light:rocket-launch-outline" value={p().icon ?? ''} onInput={e => patch({ icon: e.currentTarget.value })} />
            <Input label="Tamaño (px)" type="number" min="16" value={p().maxWidth ?? 48} onInput={e => patch({ maxWidth: Number(e.currentTarget.value) || 48 })} />
            <Select label="Color" options={TONE_OPTIONS} value={p().tone ?? 'primary'} onChange={v => patch({ tone: v as 'default' | 'muted' | 'primary' })} />
            <Select label="Alineación" options={ALIGN_OPTIONS} value={p().align ?? 'center'} onChange={v => patch({ align: v as 'left' | 'center' | 'right' })} />
          </Show>

          <Show when={props.widget!.type === 'spacer'}>
            <Select
              label="Altura"
              options={[
                { value: 'sm', title: 'Pequeña' },
                { value: 'md', title: 'Normal' },
                { value: 'lg', title: 'Grande' },
                { value: 'xl', title: 'Muy grande' }
              ]}
              value={p().height ?? 'md'}
              onChange={v => patch({ height: v as 'sm' | 'md' | 'lg' | 'xl' })}
            />
          </Show>

          <Show when={props.widget!.type === 'html'}>
            <Textarea label="Código HTML" rows={10} value={p().code ?? ''} onInput={e => patch({ code: e.currentTarget.value })} />
            <p class="text-xs text-foreground-subtle">Se inserta tal cual en la página. Úsalo sólo con código que tú controles.</p>
          </Show>
        </div>
      </Show>

      <Show when={!props.widget && props.sectionStyle}>
        <div class="space-y-4">
          <Select
            label="Fondo"
            options={[
              { value: 'none', title: 'Sin fondo' },
              { value: 'surface', title: 'Claro' },
              { value: 'muted', title: 'Gris suave' },
              { value: 'primary', title: 'Color de marca' },
              { value: 'dark', title: 'Oscuro' }
            ]}
            value={props.sectionStyle!.background ?? 'none'}
            onChange={v => props.onPatchSection({ background: v as SectionStyleType['background'] })}
          />
          <ImageField label="Imagen de fondo" value={props.sectionStyle!.backgroundImage} onChange={value => props.onPatchSection({ backgroundImage: value })} onError={props.onError} />
          <Select
            label="Espacio arriba y abajo"
            options={[
              { value: 'none', title: 'Ninguno' },
              { value: 'sm', title: 'Poco' },
              { value: 'md', title: 'Normal' },
              { value: 'lg', title: 'Amplio' },
              { value: 'xl', title: 'Muy amplio' }
            ]}
            value={props.sectionStyle!.paddingY ?? 'lg'}
            onChange={v => props.onPatchSection({ paddingY: v as SectionStyleType['paddingY'] })}
          />
          <Select
            label="Ancho del contenido"
            options={[
              { value: 'boxed', title: 'Centrado' },
              { value: 'wide', title: 'Ancho' },
              { value: 'full', title: 'Todo el ancho' }
            ]}
            value={props.sectionStyle!.width ?? 'boxed'}
            onChange={v => props.onPatchSection({ width: v as SectionStyleType['width'] })}
          />
          <Select
            label="Separación entre columnas"
            options={[
              { value: 'sm', title: 'Poca' },
              { value: 'md', title: 'Normal' },
              { value: 'lg', title: 'Amplia' }
            ]}
            value={props.sectionStyle!.gap ?? 'md'}
            onChange={v => props.onPatchSection({ gap: v as SectionStyleType['gap'] })}
          />
        </div>
      </Show>

      <Show when={!props.widget && !props.sectionStyle}>
        <div class="space-y-4">
          <Input label="Título de la pestaña" placeholder="Se usa el nombre de la página" value={props.pageSettings.title ?? ''} onInput={e => props.onPatchPage({ title: e.currentTarget.value })} />
          <Textarea label="Descripción para buscadores" rows={3} value={props.pageSettings.description ?? ''} onInput={e => props.onPatchPage({ description: e.currentTarget.value })} />
          <p class="text-xs text-foreground-subtle">Selecciona un elemento del lienzo para editar sus propiedades.</p>
        </div>
      </Show>
    </div>
  )
}

export default PageInspector
